// Crear una promo en varias cadenas: las elegidas, las copias ya creadas y el aviso de cómo salió.
import { useState } from 'react';
import { toast } from 'sonner';
import type { SalePromotion } from '../api/promotionsContract';
import { createPerRetailer } from './createPerRetailer';
import { perRetailerFailureCopy, perRetailerSuccessDescription } from './perRetailerCopy';
import { saveSuccessCopy } from './promotionActionsCopy';
import { SAVE_ERROR_TITLE } from './salePromotionSaveError';

export const RETAILER_REQUIRED_MESSAGE = 'Elige la cadena de la promo.';

export type MultiRetailerCreateResult<F> =
    | { ok: true; first: SalePromotion }
    | { ok: false; field?: F };

export function useMultiRetailerCreate(nameOf: (retailerId: string) => string) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    // Las copias del borrador que ya se crearon: un reintento no las duplica.
    const [created, setCreated] = useState<SalePromotion[]>([]);
    const [error, setError] = useState<string | undefined>(undefined);

    const reset = (initial: string[]) => {
        setSelectedIds(initial);
        setCreated([]);
        setError(undefined);
    };

    const toggle = (retailerId: string) => {
        setSelectedIds((current) =>
            current.includes(retailerId)
                ? current.filter((one) => one !== retailerId)
                : [...current, retailerId],
        );
        setError(undefined);
    };

    /** Una copia por cadena elegida que falte; avisa y devuelve la de la primera cadena. */
    const run = async <F>(
        create: (retailerId: string) => Promise<SalePromotion>,
        describeError: (error: unknown) => { message: string; field?: F },
    ): Promise<MultiRetailerCreateResult<F>> => {
        if (selectedIds.length === 0) {
            setError(RETAILER_REQUIRED_MESSAGE);
            toast.error(SAVE_ERROR_TITLE, { description: RETAILER_REQUIRED_MESSAGE });
            return { ok: false };
        }
        const done = new Set(created.map((promo) => promo.retailerId));
        const outcome = await createPerRetailer(
            selectedIds.filter((id) => !done.has(id)),
            create,
        );
        const copies = [...created, ...outcome.created.map((one) => one.result)];
        setCreated(copies);
        const createdNames = copies.map((promo) => nameOf(promo.retailerId));
        if (outcome.failed.length > 0) {
            const failures = outcome.failed.map((one) => ({
                name: nameOf(one.retailerId),
                ...describeError(one.error),
            }));
            const copy = perRetailerFailureCopy(
                createdNames,
                failures.map((one) => ({ name: one.name, reason: one.message })),
            );
            toast.error(copy.title, { description: copy.description });
            return { ok: false, field: copies.length > 0 ? undefined : failures[0].field };
        }
        const first = copies.find((promo) => promo.retailerId === selectedIds[0]) ?? copies[0];
        toast.success(saveSuccessCopy(first, true).title, {
            description: perRetailerSuccessDescription(first.name, createdNames),
        });
        return { ok: true, first };
    };

    return {
        selectedIds,
        createdIds: created.map((promo) => promo.retailerId),
        locked: created.length > 0,
        error,
        reset,
        toggle,
        run,
    };
}
